import { toast } from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { applyStatusToCachedBoard } from "features/appointments/utils/applyStatusToCachedBoard";
import { useHandleNetworkError } from "utils/useHandleNetworkError";

export const useUpdateAppointmentStatus = (onSuccess) => {
  const queryClient = useQueryClient();

  const handleNetworkError = useHandleNetworkError("Error saving appointment");

  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useMutation({
    // This mutation reports its own failure, so the global handler in
    // GlobalLayout must stay quiet — otherwise one error raises two toasts.
    meta: { skipGlobalError: true },
    mutationFn: async ({ id, status }) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/appointment/id/${id}/updatestatus/${status}`
      );
      return response.data;
    },
    // Repaint the card before the round trip. Reception marks a patient arrived and looks
    // straight back at the board to check it took; waiting for the refetch meant the
    // colour lagged by a second or more and the click looked as though it had been
    // missed — which is how a patient ends up sitting unseen.
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ["appointments"] });

      const previous = queryClient.getQueriesData({ queryKey: ["appointments"] });

      queryClient.setQueriesData({ queryKey: ["appointments"] }, (board) =>
        applyStatusToCachedBoard(board, id, status)
      );

      return { previous };
    },
    onSuccess: () => {
      toast.success("Data saved successfully!");
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["monthSlotCounts"] });
      onSuccess?.();
    },
    // Put back exactly what was there. A failed status change that leaves the optimistic
    // colour on screen is worse than no optimism at all: the board would claim a state the
    // server rejected.
    onError: (error, variables, context) => {
      context?.previous?.forEach(([queryKey, board]) => {
        queryClient.setQueryData(queryKey, board);
      });
      handleNetworkError(error);
    },
  });
};
